import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, deleteJob } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { logEvent } from "../lib/log";
import WalkthroughModal from "../components/WalkthroughModal";
import { getUser } from "../lib/auth";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

const STATUS_CONFIG = {
  pending:          { label: "Pending",          color: "#f59e0b", bg: "rgba(245,158,11,0.1)"  },
  trial_done:       { label: "Trial Done",        color: "#10b981", bg: "rgba(16,185,129,0.08)" },
  processing:       { label: "Processing",        color: BLUE,      bg: "rgba(1,118,206,0.1)"   },
  complete:         { label: "Complete",          color: "#10b981", bg: "rgba(16,185,129,0.08)" },
  failed:           { label: "Failed",            color: "#ef4444", bg: "rgba(239,68,68,0.1)"   },
  paid:             { label: "Paid",              color: "#10b981", bg: "rgba(16,185,129,0.08)" },
  uploaded:         { label: "Uploaded",          color: "#64748b", bg: "rgba(100,116,139,0.1)" },
  awaiting_payment: { label: "Awaiting Payment",  color: ORANGE,    bg: "rgba(243,116,40,0.1)"  },
};

function displayStatus(job) {
  if (job.status === "pending" && (job.processed_count > 0 || job.processed_records > 0)) {
    return "trial_done";
  }
  return job.status;
}

function statusBadge(job) {
  const key = displayStatus(job);
  const cfg = STATUS_CONFIG[key] || { label: key, color: "#64748b", bg: "rgba(100,116,139,0.1)" };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33`,
      borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 9px",
      textTransform: "uppercase", letterSpacing: "0.05em",
    }}>{cfg.label}</span>
  );
}

function formatDate(raw) {
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatRows(n) {
  if (!n && n !== 0) return "—";
  return n.toLocaleString();
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [jobs, setJobs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({ total: 0, processing: 0, uploaded: 0, enriched: 0 });
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(!getUser()?.walkthrough_seen_at);

  useEffect(() => { logEvent('page_view', { page: 'dashboard' }); }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await apiFetch("/api/jobs");
      const list = res.jobs || res || [];
      setJobs(list);
      setStats({
        total:      list.length,
        processing: list.filter(j => ["processing","pending"].includes(j.status)).length,
        uploaded:   list.reduce((sum, j) => sum + (j.vendor_count || j.total_records || 0), 0),
        enriched:   list.reduce((sum, j) => sum + (j.processed_records || j.processed_count || 0), 0),
      });
      setError("");
    } catch (err) {
      setError("Could not load jobs. " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 15000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  async function handleDelete(jobId) {
    setDeleteInFlight(true);
    try {
      await deleteJob(jobId);
      setJobs(prev => prev.filter(j => j.job_id !== jobId));
    } catch (e) {
      alert("Could not delete job: " + (e.message || "Unknown error"));
    } finally {
      setDeletingJobId(null);
      setDeleteInFlight(false);
    }
  }

  return (
    <div style={styles.page}>
      {showWalkthrough && <WalkthroughModal onClose={() => setShowWalkthrough(false)} />}
      <div style={styles.topStripe} />
      <div style={styles.body}>
        <Sidebar active="dashboard" />

        <main style={styles.main}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.pageTitle}>Dashboard</h1>
              <p style={styles.pageSub}>Your file cleaning jobs</p>
            </div>
            <Link to="/upload" style={{ textDecoration: "none" }}>
              <button style={styles.primaryBtn}>+ New upload</button>
            </Link>
          </div>

          {/* Stats */}
          <div style={styles.statsRow}>
            <StatCard label="Total Jobs"        value={stats.total}               border={BLUE}   />
            <StatCard label="In Progress"       value={stats.processing}          border={ORANGE} />
            <StatCard label="Vendors Uploaded"  value={formatRows(stats.uploaded)} border={BLUE}  />
            <StatCard label="Vendors Enriched"  value={formatRows(stats.enriched)} border={ORANGE} />
          </div>

          {/* Jobs table */}
          <div style={styles.tableCard}>
            <div style={styles.tableHeader}>
              <span style={styles.tableTitle}>Recent jobs</span>
              <button onClick={fetchJobs} style={styles.refreshBtn}>↻ Refresh</button>
            </div>

            {loading && (
              <div style={styles.emptyState}>
                <div style={styles.spinner} />
                <p style={{ color: "#64748b", marginTop: 16 }}>Loading jobs…</p>
              </div>
            )}

            {!loading && error && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 32 }}>⚠</div>
                <p style={{ color: "#f87171", marginTop: 12 }}>{error}</p>
                <button onClick={fetchJobs} style={styles.retryBtn}>Retry</button>
              </div>
            )}

            {!loading && !error && jobs.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 48 }}>📂</div>
                <p style={{ color: "#64748b", marginTop: 12, fontSize: 15 }}>No jobs yet</p>
                <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>Upload a file to start cleaning your master data.</p>
                <Link to="/upload" style={{ textDecoration: "none" }}>
                  <button style={{ ...styles.primaryBtn, marginTop: 20 }}>Upload your first file →</button>
                </Link>
              </div>
            )}

            {!loading && !error && jobs.length > 0 && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["ID", "File name", "Uploaded", "Vendors", "Enriched", "Status", "Actions"].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job, i) => (
                    <tr key={job.job_id || i} style={styles.tr}>
                      <td style={{ ...styles.td, color: "#94a3b8", fontSize: 12 }}>#{job.job_id}</td>
                      <td style={styles.td}>
                        <span style={styles.filename}>{job.original_filename || "—"}</span>
                      </td>
                      <td style={styles.td}>{formatDate(job.created_at || job.created_on_utc)}</td>
                      <td style={styles.td}>
                        {(job.vendor_count || job.total_records) > 0
                          ? <Link to={`/jobs/${job.job_id}/data`} style={styles.countLink}>
                              {formatRows(job.vendor_count || job.total_records)}
                              {job.address_count > job.vendor_count && (
                                <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: 4 }}>
                                  ({job.address_count} addr)
                                </span>
                              )}
                            </Link>
                          : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={styles.td}>
                        {(job.processed_records > 0 || job.processed_count > 0)
                          ? <Link to={`/results/${job.job_id}/records?filter=all`} style={styles.countLink}>
                              {formatRows(job.processed_records || job.processed_count)}
                            </Link>
                          : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={styles.td}>{statusBadge(job)}</td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <button style={styles.outlineBtn} onClick={() => navigate(`/jobs/${job.job_id}/data`)}>
                            View data
                          </button>
                          {(job.status === "complete" || job.status === "paid" || job.processed_records > 0 || job.processed_count > 0) && (
                            <button style={styles.primaryBtnSm} onClick={() => navigate(`/results/${job.job_id}`)}>
                              Results →
                            </button>
                          )}
                          {(job.status === "uploaded" || (job.status === "pending" && !job.processed_count && !job.processed_records)) && (
                            <button style={styles.primaryBtnSm} onClick={() => navigate(`/process/${job.job_id}`)}>
                              Process
                            </button>
                          )}
                          {displayStatus(job) === "trial_done" && (
                            <button style={{ ...styles.primaryBtnSm, background: "#10b981" }} onClick={() => navigate(`/process/${job.job_id}`)}>
                              Process remaining →
                            </button>
                          )}
                          {job.status === "awaiting_payment" && (
                            <button style={{ ...styles.primaryBtnSm, background: ORANGE }} onClick={() => navigate(`/process/${job.job_id}`)}>
                              Complete payment →
                            </button>
                          )}
                          {job.status !== "processing" && (
                            deletingJobId === job.job_id
                              ? <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <button
                                    style={{ ...styles.deleteConfirmBtn, opacity: deleteInFlight ? 0.6 : 1 }}
                                    disabled={deleteInFlight}
                                    onClick={() => handleDelete(job.job_id)}
                                  >
                                    {deleteInFlight ? "Deleting…" : "Confirm delete"}
                                  </button>
                                  <button style={styles.cancelBtn} onClick={() => setDeletingJobId(null)}>Cancel</button>
                                </span>
                              : <button style={styles.deleteBtn} onClick={() => setDeletingJobId(job.job_id)} title="Delete job">✕</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function StatCard({ label, value, border }) {
  return (
    <div style={{ ...styles.statCard, borderTopColor: border }}>
      <div style={{ ...styles.statValue, color: border }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

const styles = {
  page:        { display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:   { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:        { display: "flex", flex: 1 },
  main:        { flex: 1, padding: "40px 48px", overflowY: "auto", background: "#f8fafc" },
  header:      { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32 },
  pageTitle:   { color: "#0f172a", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:     { color: "#64748b", fontSize: 14, margin: "4px 0 0" },
  primaryBtn:  { background: ORANGE, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "10px 20px" },
  primaryBtnSm:{ background: ORANGE, border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "5px 12px", whiteSpace: "nowrap" },
  outlineBtn:  { background: "transparent", border: `1px solid ${BLUE}`, borderRadius: 6, color: BLUE, cursor: "pointer", fontSize: 12, fontWeight: 500, padding: "5px 12px", whiteSpace: "nowrap" },
  statsRow:    { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 },
  statCard:    { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, borderTop: "4px solid", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  statValue:   { fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 4 },
  statLabel:   { color: "#64748b", fontSize: 13, fontWeight: 500 },
  tableCard:   { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  tableHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #e2e8f0" },
  tableTitle:  { color: "#0f172a", fontWeight: 600, fontSize: 15 },
  refreshBtn:  { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, color: "#64748b", cursor: "pointer", fontSize: 13, padding: "5px 12px" },
  emptyState:  { textAlign: "center", padding: "60px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  spinner:     { width: 32, height: 32, border: "3px solid #e2e8f0", borderTop: `3px solid ${BLUE}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  retryBtn:    { marginTop: 16, background: "transparent", border: `1px solid ${BLUE}`, borderRadius: 6, color: BLUE, cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  table:       { width: "100%", borderCollapse: "collapse" },
  th:          { color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 24px", textAlign: "left", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  tr:          { borderBottom: "1px solid #f1f5f9" },
  td:          { color: "#374151", fontSize: 14, padding: "14px 24px", verticalAlign: "middle" },
  filename:         { color: "#0f172a", fontWeight: 500, maxWidth: 220, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  countLink:        { color: BLUE, fontWeight: 600, textDecoration: "none", fontSize: 14 },
  deleteBtn:        { background: "transparent", border: "1px solid #fca5a5", borderRadius: 6, color: "#ef4444", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "4px 10px", whiteSpace: "nowrap" },
  deleteConfirmBtn: { background: "#ef4444", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "4px 10px", whiteSpace: "nowrap" },
  cancelBtn:        { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, color: "#64748b", cursor: "pointer", fontSize: 12, padding: "4px 10px", whiteSpace: "nowrap" },
};
